<script lang="ts" setup>
import {ref, nextTick, computed} from 'vue';
import ExploreLibraryCard from '@/vue/molecules/recco/explore-library-card.vue';
import BackLink from '@/vue/molecules/back-link.vue';
import {pagesPath} from '@/ts/router/pages-path';
import type {ExploreLibraryItemDTO} from '@/ts/types/dto/recco/library.dto';
import {withConsumed} from '@/ts/utils/consumed-overlay';
import {useViewedContentStore} from '@/ts/store/viewed-content-store';

/**
 * A full-page grid of library items that loads a page at a time: header with the backend's match
 * count, the two failure states with their retries, the live region, and the sentinel the caller
 * watches. Shared by the topic "View all" and the partner band's, which differ only in what they
 * ask the backend for and what they are called.
 *
 * The class names are the topic page's, from before this was shared. Kept as they are because the
 * E2E suite and the screenshot baselines select on them.
 */
const props = defineProps<{
  title: string;
  /** "N items", from the backend's match count. */
  countLabel: string;
  /** Visually hidden "N of M shown", the only signal a screen reader gets that the grid grew. */
  statusLabel: string;
  /** i18n key the cards carry as their back-navigation label. */
  backLabel: string;
  items: ExploreLibraryItemDTO[];
  loading: boolean;
  error: boolean;
  loadingMore: boolean;
  /** Whether to offer the retry under the grid — a failed page, or a walk that stopped short. */
  showRetryMore: boolean;
  /**
   * Hands the caller the sentinel element as it mounts. A callback rather than a ref prop: a ref
   * passed through a template is unwrapped on the way in, so the child would receive the element
   * and not the box to put it in.
   */
  setSentinel(el: HTMLElement | null): void;
}>();

const emit = defineEmits<{retryFirst: []; retryMore: []}>();

const grid = ref<HTMLElement | null>(null);
const viewedContent = useViewedContentStore();

// Applied here rather than in the card, the same way explore-library-rail and block-content-band
// do it. Without it a grid never shows a consumed marker at all — which would make the staleness
// refresh this page does on activation pointless, since the marker it refreshes never rendered.
const rows = computed<ExploreLibraryItemDTO[]>(() =>
  props.items.map((item) => withConsumed(item, viewedContent.hasViewed(item.content?.id))));

const itemKey = (item: ExploreLibraryItemDTO): string =>
  `${item.contentType}-${item.content?.id ?? item.challenge?.id}`;

/**
 * Both retries delete the button that was clicked, which drops focus to `<body>` — for a keyboard
 * or screen-reader user that means losing their place in the middle of a long grid. Exposed so the
 * caller can move focus to the row that arrived once its fetch settles.
 */
const focusRow = async(index: number): Promise<void> => {
  await nextTick();
  const row = grid.value?.children.item(index);
  if (row instanceof HTMLElement) {
    row.focus();
  }
};

defineExpose({focusRow});
</script>

<template>
  <div class="topic-page">
    <back-link :label="$t('recco_explore')" :to="pagesPath.main.explore" :hide-arrow="true"/>

    <div class="topic-header">
      <h1 class="topic-title">
        {{ props.title }}
      </h1>
      <span v-if="!props.loading && !props.error" class="topic-count">{{ props.countLabel }}</span>
    </div>

    <!-- First page: a spinner rather than an empty grid, which reads as an empty collection. -->
    <div v-if="props.loading" class="topic-initial-loading">
      <q-spinner-dots color="primary" size="32px"/>
    </div>

    <div v-else-if="props.error" class="topic-error" role="alert">
      <p class="topic-error__title">
        {{ $t('explore_filter_error_title') }}
      </p>
      <p class="topic-error__helper">
        {{ $t('explore_filter_error_helper') }}
      </p>
      <button type="button" class="topic-error__retry" @click="emit('retryFirst')">
        {{ $t('explore_filter_retry') }}
      </button>
    </div>

    <!-- Only a genuinely empty collection gets the empty state. Zero rows against a non-zero count
         means the walk stopped short, which the branch below reports with a retry. -->
    <p v-else-if="props.items.length === 0 && !props.showRetryMore" class="topic-empty">
      {{ $t('explore_topic_empty') }}
    </p>

    <template v-else>
      <!-- No role="feed" here: it requires every child to be role="article", and the card is
           shared with the Explore rails, so giving it that role would change them too. The
           role="status" line below is what announces instead. -->
      <div ref="grid" class="topic-grid">
        <explore-library-card
          v-for="item in rows"
          :key="itemKey(item)"
          :item="item"
          :back-label="props.backLabel"
          uniform-labels
        />
      </div>

      <!-- The visible count is a pure function of the total, so it never changes as pages arrive
           and announces nothing. This is the region that does. -->
      <p class="sr-only" role="status">
        {{ props.statusLabel }}
      </p>

      <!-- Watched rather than scroll-listened: the observer fires once per crossing instead of on
           every scroll event, and needs no knowledge of which ancestor scrolls. -->
      <div :ref="(el) => props.setSentinel(el as HTMLElement | null)" class="topic-sentinel" aria-hidden="true"/>

      <div v-if="props.loadingMore" class="topic-loading">
        <q-spinner-dots color="primary" size="32px"/>
      </div>

      <!-- A page that fails mid-scroll keeps the rows already loaded and offers a retry, instead
           of replacing the whole grid with an error. -->
      <div v-else-if="props.showRetryMore" class="topic-more-error" role="alert">
        <p class="topic-more-error__text">
          {{ $t('explore_filter_error_helper') }}
        </p>
        <button type="button" class="topic-more-error__retry" @click="emit('retryMore')">
          {{ $t('explore_filter_retry') }}
        </button>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.topic-page {
  @include page-alignment;

  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-4);
}

.topic-header {
  display: flex;
  align-items: baseline;
  gap: var(--Spacing-spacing-3);
}

.topic-title {
  @include font-h1-bold;

  margin: 0;
}

.topic-count {
  @include font-body;

  color: var(--Neutral-UI-neutral-ui-100);
}

// The mockup lays the full list out as a wrapping grid rather than the rails used on Explore
// itself, four across at desktop and collapsing down with the viewport.
.topic-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--Spacing-spacing-4);

  @include screen-sm {
    grid-template-columns: repeat(2, 1fr);
  }

  @include screen-md {
    grid-template-columns: repeat(3, 1fr);
  }

  @include screen-lg {
    grid-template-columns: repeat(4, 1fr);
  }
}

.topic-sentinel {
  height: 1px;
}

.topic-initial-loading,
.topic-loading {
  display: flex;
  justify-content: center;
  padding: var(--Spacing-spacing-5) 0;
}

.topic-more-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--Spacing-spacing-3);
  padding: var(--Spacing-spacing-5) 0;
}

.topic-more-error__text {
  @include font-body;

  color: var(--Neutral-UI-neutral-ui-60);
  margin: 0;
}

.topic-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--Spacing-spacing-3);
  padding: var(--Spacing-spacing-5) 0;
  text-align: center;
}

.topic-error__title {
  @include font-h3-bold;

  color: var(--Neutral-UI-neutral-ui-100);
  margin: 0;
}

.topic-error__helper {
  @include font-body;

  color: var(--Neutral-UI-neutral-ui-60);
  margin: 0;
}

// Same affordance as the retry under Explore's filtered results (`.explore-empty__clear`).
.topic-error__retry,
.topic-more-error__retry {
  @include focus-visible;
  @include font-body-sm-bold;

  padding: var(--Spacing-spacing-2) var(--Spacing-spacing-6);
  border: none;
  border-radius: var(--button-border-radius);
  background-color: var(--Neutral-UI-neutral-ui-100);
  color: var(--Neutral-UI-neutral-ui-0);
  cursor: pointer;
}

.topic-empty {
  @include font-body;

  color: var(--Neutral-UI-neutral-ui-100);
  text-align: center;
  padding: var(--Spacing-spacing-5) 0;
  margin: 0;
}
</style>
